import * as React from 'react'

import { SearchIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Content,
  Header,
  Padding,
  Thickness,
  View,
} from '~/client/src/shared/components/Layout'
import Text from '~/client/src/shared/components/Typography/Text'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import { LOGOUT } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import StruxhubInput from '../../components/StruxhubInputs/StruxhubInput'
import { itemPredicate } from '../../utils/collections'
import { EMPTY_STRING } from '../../utils/usefulStrings'
import ProjectBlock from './components/ProjectBlock/ProjectBlock'

import './Projects.scss'

interface IProjectsView {
  state?: InitialState
  projectsStore?: ProjectsStore
  companiesStore?: CompaniesStore
  eventsStore?: EventsStore
}

const SEARCH_INPUT_DELAY = 350

@inject('state', 'projectsStore', 'companiesStore', 'eventsStore')
@observer
export default class ProjectsListView extends React.Component<IProjectsView> {
  @observable private currentSearchValue = EMPTY_STRING

  public constructor(props: IProjectsView) {
    super(props)
    makeObservable(this)
  }

  public componentDidMount() {
    this.autoSelectSingleProjectIfReady()
  }

  public componentDidUpdate() {
    // The user-visible /projects entry path for single-project users
    // is handled at the bootstrap layer (commonEvents.ts INIT_APP_2 pushes
    // '/' so the catch-all redirects into the project). This auto-select
    // is the fallback for the rarer case where the user manually
    // navigates to /projects post-bootstrap with only one project — they
    // shouldn't have to click the single block. Guard with hasAutoSelected
    // so we fire exactly once per mount.
    this.autoSelectSingleProjectIfReady()
  }

  private autoSelectSingleProjectIfReady() {
    if (this.hasAutoSelected) {
      return
    }
    const { projectsStore } = this.props
    if (projectsStore.list.length === 1) {
      this.hasAutoSelected = true
      projectsStore.selectProject(projectsStore.list[0].id)
    }
  }

  private hasAutoSelected = false

  public render() {
    const {
      state: { userActiveProjectSettings },
      companiesStore,
      projectsStore: { list: projects },
    } = this.props

    if (!projects.length) {
      return (
        <View className="projects-list">
          <Content>
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:py-16 tw:px-6 tw:text-center">
              <span className="tw:text-muted-foreground">
                {Localization.translator.noProjectsAvailableTitle}
              </span>
              <Button variant="link" onClick={this.handleLogout}>
                {Localization.translator.logOut}
              </Button>
            </div>
          </Content>
        </View>
      )
    }

    const projectBlocks = projects
      .filter(p => itemPredicate(this.currentSearchValue, p))
      .map(p => (
        <Padding padding={Thickness.fromTRBL(0, 0, 10, 10)} key={p.id}>
          <ProjectBlock project={p} onClick={this.handleProjectSelected} />
        </Padding>
      ))

    return (
      <View className="projects-list">
        <Header>
          <Padding padding={Thickness.fromTRBL(20, 0, 10, 18)}>
            <Text bold className="header">
              {companiesStore.getCompanyNameById(
                userActiveProjectSettings?.companyId,
                projects.length
                  ? Localization.translator.chooseProject
                  : EMPTY_STRING,
              )}
            </Text>
          </Padding>
          <Padding padding={Thickness.fromTRBL(0, 0, 10, 18)}>
            <div className="bb-light-input-border" />
          </Padding>
          <Padding padding={Thickness.fromTRBL(5, 26, 5, 26)}>
            <StruxhubInput
              type="text"
              withDelay
              inputDelay={SEARCH_INPUT_DELAY}
              value={this.currentSearchValue}
              customRightIcon={<SearchIcon size={16} />}
              isMinimalisticMode
              placeholder={Localization.translator.search}
              onChange={this.handleSearchChange}
            />
          </Padding>
        </Header>
        <Content scrollable={true}>
          <Padding padding={Thickness.symmetric(18, 10)}>
            {projectBlocks}
          </Padding>
        </Content>
      </View>
    )
  }

  @action.bound
  private handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.currentSearchValue = event.target.value
  }

  private handleProjectSelected = (id: string) => {
    this.props.projectsStore.selectProject(id)
  }

  private handleLogout = () => {
    this.props.eventsStore.dispatch(LOGOUT)
  }
}
